import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import { resolveHoverActive } from "../input/resolveHoverActive";
import {
  createUpgradeHoverRepeater,
  type UpgradeHoverRepeater,
  type UpgradeHoverRepeatContext,
  type UpgradeHoverRepeatResult,
} from "@/features/hoverIdle/hover/upgradeHoverRepeater";
import type {
  UpgradeRepeatInitialIntervalSource,
  UpgradeRepeatSpeedMultiplierSource,
} from "./upgradeRepeatTiming";

interface UpgradeInteractionControllerOptions {
  readonly button: HTMLButtonElement;
  readonly card: HTMLElement;
  readonly tooltip: HTMLElement;
  readonly tooltipAnchor?: HTMLElement;
  readonly pointerModel: HoverPointerModel;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  readonly onUpgrade: (
    context: UpgradeHoverRepeatContext,
  ) => UpgradeHoverRepeatResult;
  readonly onTooltipShow?: () => void;
  readonly onUpgradeBurstStart?: () => void;
  readonly onUpgradeBurstEnd?: () => void;
}

export class UpgradeInteractionController implements Disposable {
  private readonly button: HTMLButtonElement;
  private readonly tooltip: HTMLElement;
  private readonly tooltipAnchor: HTMLElement;
  private readonly pointerModel: HoverPointerModel;
  private readonly onUpgrade: (
    context: UpgradeHoverRepeatContext,
  ) => UpgradeHoverRepeatResult;
  private readonly onTooltipShow: (() => void) | null;
  private readonly repeater: UpgradeHoverRepeater;
  private readonly unsubscribePointer: () => void;
  private isTooltipAnchorHovered = false;
  private isTooltipAnchorFocused = false;
  private isButtonPointerHovering = false;
  private isInputLocked = false;
  private isTooltipVisible = false;

  private readonly handleButtonFocus = (): void => {
    if (this.isButtonBlocked()) {
      return;
    }
    if (this.isButtonPointerHovering) {
      return;
    }

    this.onUpgrade({
      nextIntervalMs: this.repeater.getInitialIntervalMs(),
    });
  };

  private readonly handleTooltipAnchorPointerEnter = (): void => {
    this.isTooltipAnchorHovered = true;
    this.syncTooltipVisibility();
  };

  private readonly handleTooltipAnchorPointerLeave = (): void => {
    this.isTooltipAnchorHovered = false;
    this.syncTooltipVisibility();
  };

  private readonly handleTooltipAnchorFocus = (): void => {
    this.isTooltipAnchorFocused = true;
    this.syncTooltipVisibility();
  };

  private readonly handleTooltipAnchorBlur = (): void => {
    this.isTooltipAnchorFocused = false;
    this.syncTooltipVisibility();
  };

  constructor(options: UpgradeInteractionControllerOptions) {
    this.button = options.button;
    this.tooltip = options.tooltip;
    this.tooltipAnchor = options.tooltipAnchor ?? options.card;
    this.pointerModel = options.pointerModel;
    this.onUpgrade = options.onUpgrade;
    this.onTooltipShow = options.onTooltipShow ?? null;

    this.repeater = createUpgradeHoverRepeater({
      initialIntervalMs: options.initialIntervalMs,
      speedMultiplier: options.speedMultiplier,
      onBurstStart: options.onUpgradeBurstStart,
      onBurstEnd: options.onUpgradeBurstEnd,
      onFire: ({ nextIntervalMs }) => {
        return this.onUpgrade({ nextIntervalMs });
      },
    });

    this.unsubscribePointer = this.pointerModel.subscribe(() => {
      this.refreshButtonHover();
    });
    this.refreshButtonHover();

    this.button.addEventListener("focus", this.handleButtonFocus);
    this.tooltipAnchor.addEventListener(
      "pointerenter",
      this.handleTooltipAnchorPointerEnter,
    );
    this.tooltipAnchor.addEventListener(
      "pointerleave",
      this.handleTooltipAnchorPointerLeave,
    );
    this.tooltipAnchor.addEventListener("focus", this.handleTooltipAnchorFocus);
    this.tooltipAnchor.addEventListener("blur", this.handleTooltipAnchorBlur);
  }

  dispose(): void {
    this.hideTooltip();
    this.unsubscribePointer();
    this.repeater.dispose();
    this.button.removeEventListener("focus", this.handleButtonFocus);
    this.tooltipAnchor.removeEventListener(
      "pointerenter",
      this.handleTooltipAnchorPointerEnter,
    );
    this.tooltipAnchor.removeEventListener(
      "pointerleave",
      this.handleTooltipAnchorPointerLeave,
    );
    this.tooltipAnchor.removeEventListener(
      "focus",
      this.handleTooltipAnchorFocus,
    );
    this.tooltipAnchor.removeEventListener(
      "blur",
      this.handleTooltipAnchorBlur,
    );
  }

  hideTooltip(): void {
    this.isTooltipAnchorHovered = false;
    this.isTooltipAnchorFocused = false;
    this.syncTooltipVisibility();
  }

  setInputLocked(locked: boolean): void {
    if (locked === this.isInputLocked) {
      return;
    }
    this.isInputLocked = locked;
    if (locked) {
      this.isButtonPointerHovering = false;
      this.repeater.setEngaged(false);
      this.hideTooltip();
      return;
    }
    this.refreshButtonHover();
  }

  isTooltipShown(): boolean {
    return this.isTooltipVisible;
  }

  private refreshButtonHover(): void {
    const snapshot = this.pointerModel.getSnapshot();
    const rect = this.button.getBoundingClientRect();
    const next = this.isButtonBlocked()
      ? false
      : resolveHoverActive(snapshot, rect);
    if (next === this.isButtonPointerHovering) {
      return;
    }
    this.isButtonPointerHovering = next;
    this.repeater.setEngaged(next);
  }

  private isButtonBlocked(): boolean {
    return this.isInputLocked || this.button.closest("[inert]") !== null;
  }

  private syncTooltipVisibility(): void {
    const isVisible =
      this.isTooltipAnchorHovered || this.isTooltipAnchorFocused;
    if (isVisible && !this.isTooltipVisible) {
      this.onTooltipShow?.();
    }
    this.isTooltipVisible = isVisible;
    this.tooltip.hidden = !isVisible;
    this.tooltip.setAttribute("aria-hidden", isVisible ? "false" : "true");
  }
}
